"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { FaInstagram, FaWhatsapp } from "react-icons/fa";
import { FiInfo, FiList } from "react-icons/fi";

export default function MobileMenu() {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const trigger = useRef<HTMLButtonElement>(null);
  const mobileNav = useRef<HTMLDivElement>(null);

  // close the mobile menu on click outside
  useEffect(() => {
    const clickHandler = ({ target }: { target: EventTarget | null }): void => {
      if (!mobileNav.current || !trigger.current) return;
      if (!mobileNavOpen || mobileNav.current.contains(target as Node) || trigger.current.contains(target as Node))
        return;
      setMobileNavOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close the mobile menu if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: { keyCode: number }): void => {
      if (!mobileNavOpen || keyCode !== 27) return;
      setMobileNavOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <div className='md:hidden flex items-center ml-4'>
      {/* Hamburger button */}
      <button
        ref={trigger}
        className={`hamburger ${mobileNavOpen && "active"}`}
        aria-controls='mobile-nav'
        aria-expanded={mobileNavOpen}
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
      >
        <span className='sr-only'>Menu</span>
        <svg
          className='w-5 h-5 fill-current text-slate-800 hover:text-purple-600 transition duration-150 ease-in-out'
          viewBox='0 0 20 20'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect y='2' width='20' height='2' rx='1' />
          <rect y='9' width='20' height='2' rx='1' />
          <rect y='16' width='20' height='2' rx='1' />
        </svg>
      </button>

      {/*Mobile navigation */}
      <nav
        id='mobile-nav'
        ref={mobileNav}
        className='absolute top-full z-20 left-0 w-full px-4 sm:px-6 overflow-hidden transition-all duration-300 ease-in-out'
        style={
          mobileNavOpen ? { maxHeight: mobileNav.current?.scrollHeight, opacity: 1 } : { maxHeight: 0, opacity: 0.8 }
        }
      >
        <ul className='border border-transparent [background:linear-gradient(theme(colors.slate.900),_theme(colors.slate.900))_padding-box,_conic-gradient(theme(colors.slate.400),_theme(colors.slate.700)_25%,_theme(colors.slate.700)_75%,_theme(colors.slate.400)_100%)_border-box] rounded-lg px-4 py-1.5'>
          <li>
            <Link
              className='flex font-medium text-sm text-slate-300 hover:text-white py-1.5'
              href='https://www.instagram.com/rotalounge/'
            >
              <FaInstagram className='mr-1' /> Instagram
            </Link>
          </li>
          <li>
            <Link
              className='flex font-medium text-sm text-slate-300 hover:text-white py-1.5'
              href='https://api.whatsapp.com/send?phone=5511940590576&text=Ol%C3%A1%20quero%20saber%20mais%20sobre%20a%20Rota%20Lounge'
            >
              <FaWhatsapp className='mr-1' /> WhatsApp
            </Link>
          </li>
          <li>
            <Link className='flex font-medium text-sm text-slate-300 hover:text-white py-1.5' href='/#about'>
              <FiInfo className='mr-1' /> Sobre
            </Link>
          </li>
          <li>
            <Link className='flex font-medium text-sm text-slate-300 hover:text-white py-1.5' href='/#plans'>
              <FiList className='mr-1' /> Planos
            </Link>
          </li>
          <li>
            <Link className='flex font-medium text-sm text-slate-300 hover:text-white py-1.5' href='/faq'>
              <AiOutlineQuestionCircle className='mr-1' /> FAQ
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
}
